import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import DrawerWrapper, { DrawerFooter } from '../IAP/DrawerWrapper';
import FormTextField from '../IAP/formFields/FormTextField';
import FormSelectField from '../IAP/formFields/FormSelectField';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { useAlertCategories } from './hooks/useAlertCategories';
import { DGPTAlertTrigger, DGPTAlertCategory } from '../../types/dgptAlerts';
import DataSources from '../DisasterGPT/DataSources';

type TriggerEditDrawerProps = {
  show: boolean;
  onHide: () => void;
  onSave: (trigger: DGPTAlertTrigger) => void;
  trigger?: DGPTAlertTrigger | null;
};

const TriggerEditDrawer: React.FC<TriggerEditDrawerProps> = ({
  show,
  onHide,
  onSave,
  trigger,
}) => {
  const { data: categories = [] } = useAlertCategories();
  const [selectedDataSources, setSelectedDataSources] = useState<any[]>([]);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      categoryId: '',
    },
  });

  const { handleSubmit, watch, reset } = form;
  const watchedName = watch('name');
  const watchedCategoryId = watch('categoryId');

  // Load existing trigger data when editing
  useEffect(() => {
    if (trigger) {
      reset({
        name: trigger.name,
        categoryId: trigger.categoryId,
      });
      // Convert data_sources to the format expected by DataSources component
      setSelectedDataSources(
        trigger.data_sources.map((ds) => ({
          source: ds.source,
          description: getDataSourceDescription(ds.source),
          id: ds.id || '',
        }))
      );
    } else {
      reset({
        name: '',
        categoryId: '',
      });
      setSelectedDataSources([]);
    }
  }, [trigger, reset]);

  const getDataSourceDescription = (source: string): string => {
    const descriptions: Record<string, string> = {
      News: 'News Articles',
      Files: 'Files uploaded to Pratus',
      Web: 'Web Scraping',
      'Current Weather': 'Current weather at location',
      'Forecast Weather': 'Forecast weather at location',
      'Power Outages': 'Power outages at location',
      'ERI (Energy Reliability Index)': 'Energy Reliability Index at location',
      'ICS-Active': 'Current ICS data',
      'ICS-Historic': 'Full version history of ICS data',
      'NEO Analysis': 'Noncombatant Evacuation Order Analysis',
    };
    return descriptions[source] || source;
  };

  const categoryOptions = categories.map((category: DGPTAlertCategory) => ({
    label: `Severity ${category.severity} (${category.contactMethods.join(', ')})`,
    value: category.id,
  }));

  const handleSave = async (formData: any) => {
    const triggerData: DGPTAlertTrigger = {
      name: formData.name,
      categoryId: formData.categoryId,
      data_sources: selectedDataSources.map((ds) => ({
        source: ds.source,
        id: ds.id || '',
      })),
    };

    onSave(triggerData);
    onHide();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(handleSave)(e);
  };

  if (!show) return null;

  return (
    <DrawerWrapper
      toggle={onHide}
      title={trigger ? 'Edit Trigger' : 'Add Trigger'}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleFormSubmit}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <div className="p-4">
            <div className="row">
              <FormTextField
                title="Trigger Name"
                fieldName="name"
                rules={{
                  required: {
                    value: true,
                    message: 'Trigger name is required',
                  },
                }}
                variant="full"
              />

              <FormSelectField
                title="Alert Category"
                fieldName="categoryId"
                rules={{
                  required: {
                    value: true,
                    message: 'Alert category is required',
                  },
                }}
                options={categoryOptions}
                variant="full"
                isClearable={true}
              />
            </div>

            <div className="mt-4">
              <h6>Data Sources</h6>
              <p className="text-muted mb-3">
                Select the data sources this trigger should monitor for events.
              </p>
              <DataSources
                selections={selectedDataSources}
                setSelections={setSelectedDataSources}
                usingSitrep={false}
              />
            </div>
          </div>

          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={onHide}
              >
                Cancel
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  !watchedName ||
                  !watchedCategoryId ||
                  selectedDataSources.length === 0
                }
              >
                {trigger ? 'Update' : 'Create'} Trigger
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default TriggerEditDrawer;
